import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  GAME_SERVICES,
  GAME_VIEW_STORAGE_KEY,
  SIDE_VIEW_STORAGE_KEY,
  SIDE_VIEW_WINDOW,
  WATCH_PARAM,
  arrangeHelp,
  arrangePlatform,
  frameLanded,
  fullViewUrl,
  gameViewFrame,
  gameViewUrl,
  sideViewFeatures,
  sideViewFrame,
  sideViewUrl,
  type ScreenArea,
  type SideViewSide,
  type WindowFrame,
} from './sideView';
import { useShownOnMove } from './FullscreenControl';

/**
 * Side view (sideView.ts): the full page offers it in its top bar; the side
 * window has its own small bar (Move left/right, Open game, Full window).
 * While a side window is open the full page steps aside (SideHandoff) so only
 * one page runs the TV session; closing the side window brings it back.
 *
 * Watch + putt opens LagKing first, then LagKing's own window opens the game
 * beside it (peer review F08): the second click is on a window the viewer can
 * see, and LagKing can measure where both windows landed.
 */

function screenArea(): ScreenArea {
  const s = window.screen as Screen & { availLeft?: number; availTop?: number };
  return { availLeft: s.availLeft ?? 0, availTop: s.availTop ?? 0, availWidth: s.availWidth, availHeight: s.availHeight };
}

function rememberSide(side: SideViewSide): void {
  try {
    window.localStorage.setItem(SIDE_VIEW_STORAGE_KEY, side);
  } catch {
    // Storage blocked: the pick is simply not remembered.
  }
}

export function rememberedSide(): SideViewSide {
  try {
    return window.localStorage.getItem(SIDE_VIEW_STORAGE_KEY) === 'left' ? 'left' : 'right';
  } catch {
    return 'right';
  }
}

/** Moves and sizes a window's outer frame. `window.open`'s features ask for
 *  the inner size; this corrects to the outer frame the geometry means. */
function place(win: Window, frame: WindowFrame): void {
  try {
    win.moveTo(frame.left, frame.top);
    win.resizeTo(frame.width, frame.height);
  } catch {
    // Some browsers refuse; frameLanded reports it.
  }
}

function landed(win: Window, frame: WindowFrame): boolean {
  try {
    return frameLanded(win, frame);
  } catch {
    return false;
  }
}

/** Opens (or reuses) the side window; null when the browser blocked it. */
export function openSideWindow(side: SideViewSide, watch = false): Window | null {
  rememberSide(side);
  const frame = sideViewFrame(screenArea(), side);
  const win = window.open(sideViewUrl(window.location.href, side, watch), SIDE_VIEW_WINDOW, sideViewFeatures(frame));
  if (!win) return null;
  place(win, frame);
  return win;
}

/** Side view only on a computer: a TV browser or a tablet has nowhere to put a second window. */
export function sideViewAvailable(): boolean {
  return typeof window.open === 'function'
    && window.matchMedia?.('(pointer: fine)').matches === true
    && window.screen.availWidth >= 1000;
}

const SIDE_ICON = {
  left: <path d="M4 5h16v14H4zM10 5v14" />,
  right: <path d="M4 5h16v14H4zM14 5v14" />,
};

/** The full page's Side view buttons, in the top bar next to Full screen. */
export function SideViewPicker({ onOpened, onBlocked }: { onOpened: (win: Window) => void; onBlocked: () => void }) {
  const open = (side: SideViewSide, watch: boolean) => {
    const win = openSideWindow(side, watch);
    if (win) onOpened(win);
    else onBlocked();
  };
  return (
    <div className="side-view-picker" role="group" aria-label="Side view: LagKing on a third of the screen">
      <span>Side view</span>
      {(['left', 'right'] as const).map((side) => (
        <button key={side} type="button" className="top-control-button" onClick={() => open(side, false)} aria-label={`Side view on the ${side}`}>
          <svg viewBox="0 0 24 24" aria-hidden="true">{SIDE_ICON[side]}</svg>
          {side === 'left' ? 'Left' : 'Right'}
        </button>
      ))}
      <button type="button" className="top-control-button watch-putt-button" onClick={() => open(rememberedSide(), true)}>
        Watch + putt
      </button>
    </div>
  );
}

/** The last game destination: a listed service, or the viewer's own link. */
function rememberedGame(): { url: string; custom: string } {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(GAME_VIEW_STORAGE_KEY);
  } catch {
    // Storage blocked: start from the first service.
  }
  if (stored && GAME_SERVICES.some((s) => s.url === stored)) return { url: stored, custom: '' };
  if (stored && gameViewUrl(stored)) return { url: GAME_SERVICES[0].url, custom: stored };
  return { url: GAME_SERVICES[0].url, custom: '' };
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

type WatchStep = 'pick' | 'arranged' | 'help';

/**
 * Watch + putt (Cole, 2026-10-22: "it sets up both windows"), in LagKing's
 * side window: pick the game, and LagKing opens it on the other two-thirds.
 * The game opens in a fresh window that is placed and measured while it is
 * still blank (same origin), then cut off from LagKing before it loads the
 * game. If the browser would not place it, the dialog says so and gives the
 * system's own way to arrange it.
 */
function WatchSetup({ side, onMove, onClose }: { side: SideViewSide; onMove: (side: SideViewSide) => void; onClose: () => void }) {
  const [initial] = useState(rememberedGame);
  const [url, setUrl] = useState(initial.url);
  const [custom, setCustom] = useState(initial.custom);
  const [step, setStep] = useState<WatchStep>('pick');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef<HTMLElement>(null);
  const customUrl = custom ? gameViewUrl(custom) : null;
  const target = custom ? customUrl : url;
  const serviceName = custom ? (customUrl ? new URL(customUrl).hostname : 'your link') : GAME_SERVICES.find((s) => s.url === url)?.label ?? 'the game';
  const gameSide = side === 'left' ? 'right' : 'left';

  // Modal: focus moves in, stays in, and goes back where it was on close.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    return () => before?.focus?.();
  }, []);
  useEffect(() => {
    dialogRef.current?.querySelector<HTMLElement>('.watch-setup-go, ' + FOCUSABLE)?.focus();
  }, [step]);

  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openGame = async () => {
    if (!target) {
      setError('That link does not look right. Paste the address of the page with your game.');
      return;
    }
    const frame = gameViewFrame(screenArea(), side);
    let win: Window | null = null;
    try {
      // A fresh window every time: a reused one might already hold another site.
      win = window.open('about:blank', '_blank', sideViewFeatures(frame));
    } catch {
      win = null;
    }
    if (!win) {
      setError('The browser blocked the window. Allow pop-ups for this site, then try again.');
      return;
    }
    setBusy(true);
    try {
      place(win, frame);
      // Let the browser apply the move before measuring it.
      await new Promise((resolve) => window.setTimeout(resolve, 200));
      const gameLanded = landed(win, frame);
      const ownLanded = landed(window, sideViewFrame(screenArea(), side));
      // Cut the game's window off from LagKing before it loads anything.
      win.opener = null;
      win.location.href = target;
      try {
        window.localStorage.setItem(GAME_VIEW_STORAGE_KEY, target);
      } catch {
        // Storage blocked: the pick is simply not remembered.
      }
      setError(null);
      setStep(gameLanded && ownLanded ? 'arranged' : 'help');
    } catch {
      try {
        win.close();
      } catch {
        // Already gone.
      }
      setError('The game window could not be opened. Try again, or open the game yourself beside LagKing.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="watch-setup-backdrop" onClick={onClose}>
      <section
        ref={dialogRef}
        className="watch-setup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="watch-setup-title"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <h2 id="watch-setup-title">Watch + putt</h2>
        {step === 'pick' ? (
          <>
            <p>Your game on two-thirds of the screen, LagKing on this third.</p>
            <div className="watch-setup-row">
              <span>LagKing on the</span>
              {(['left', 'right'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`top-control-button${side === option ? ' is-picked' : ''}`}
                  aria-pressed={side === option}
                  onClick={() => onMove(option)}
                >
                  {option === 'left' ? 'Left' : 'Right'}
                </button>
              ))}
            </div>
            <div className="watch-setup-services" role="group" aria-label="Where your game is">
              {GAME_SERVICES.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  className={`top-control-button${!custom && url === service.url ? ' is-picked' : ''}`}
                  aria-pressed={!custom && url === service.url}
                  onClick={() => {
                    setUrl(service.url);
                    setCustom('');
                  }}
                >
                  {service.label}
                </button>
              ))}
            </div>
            <label className="watch-setup-label" htmlFor="watch-setup-link">Or paste a link to any page</label>
            <input
              id="watch-setup-link"
              className="watch-setup-link"
              value={custom}
              onChange={(event) => setCustom(event.target.value)}
              placeholder="https://"
              spellCheck={false}
              autoComplete="off"
              aria-describedby={error ? 'watch-setup-error' : undefined}
            />
            {customUrl ? <p className="watch-setup-host">Opens <strong>{new URL(customUrl).hostname}</strong></p> : null}
            <button type="button" className="watch-setup-go" onClick={() => void openGame()} disabled={busy}>
              Open {serviceName} on the {gameSide}
            </button>
          </>
        ) : step === 'arranged' ? (
          <>
            <p>{serviceName} is on the {gameSide} two-thirds. Sign in and start your game there; putting stays here.</p>
            <button type="button" className="watch-setup-go" onClick={onClose}>Done</button>
          </>
        ) : (
          <>
            <p>{serviceName} is open, but the browser would not place the windows exactly.</p>
            <p>{arrangeHelp(arrangePlatform(navigator.userAgent))}</p>
            <button type="button" className="watch-setup-go" onClick={onClose}>Done</button>
          </>
        )}
        {step !== 'pick' ? (
          <button type="button" className="top-control-button" onClick={() => setStep('pick')}>Open another game</button>
        ) : null}
        <p id="watch-setup-error" className="watch-setup-error" role="alert">{error ?? ''}</p>
      </section>
    </div>
  );
}

/** Set on the full page while it waits behind a side window (SideHandoff), so
 *  the side window can tell its own LagKing page from any other opener. */
const HANDOFF_FLAG = 'lagkingSideHandoff';

/** The full LagKing page that opened this side window and is waiting for it,
 *  or null. A different site can open a ?side= link; its window is not ours
 *  to close into (peer review F09). */
function waitingFullPage(): Window | null {
  try {
    const opener = window.opener as (Window & { [HANDOFF_FLAG]?: boolean }) | null;
    if (!opener || opener.closed) return null;
    // Cross-origin access throws: only our own page passes.
    if (opener.location.origin !== window.location.origin) return null;
    return opener[HANDOFF_FLAG] === true ? opener : null;
  } catch {
    return null;
  }
}

/** The side window's own bar: move LagKing, open the game, or go back to the full window. */
export function SideWindowControls({ side }: { side: SideViewSide }) {
  const visible = useShownOnMove();
  const [current, setCurrent] = useState(side);
  const [watchOpen, setWatchOpen] = useState(() => new URLSearchParams(window.location.search).get(WATCH_PARAM) === '1');
  // Place the window once on load, only when a LagKing page opened it: a
  // bookmarked or installed side window keeps wherever the viewer put it.
  useEffect(() => {
    if (!waitingFullPage()) return;
    place(window, sideViewFrame(screenArea(), side));
  }, [side]);
  // The picker shows once; a reload does not bring it back.
  useEffect(() => {
    if (!watchOpen) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has(WATCH_PARAM)) return;
    url.searchParams.delete(WATCH_PARAM);
    window.history.replaceState(null, '', url.search || '/');
  }, [watchOpen]);
  const moveTo = (next: SideViewSide) => {
    if (next === current) return;
    try {
      window.moveTo(sideViewFrame(screenArea(), next).left, sideViewFrame(screenArea(), next).top);
    } catch {
      return;
    }
    rememberSide(next);
    window.history.replaceState(null, '', new URL(sideViewUrl(window.location.href, next)).search || '/');
    setCurrent(next);
  };
  const other: SideViewSide = current === 'left' ? 'right' : 'left';
  const fullWindow = () => {
    if (waitingFullPage()) {
      // The full page is waiting behind this one (SideHandoff): closing this
      // window brings it back.
      window.close();
      // A browser that refuses to close it: become the full page here.
      window.setTimeout(() => {
        if (!window.closed) window.location.href = fullViewUrl(window.location.href);
      }, 300);
      return;
    }
    window.location.href = fullViewUrl(window.location.href);
  };
  return (
    <>
      <div className={`top-controls side-window-controls${visible ? ' is-visible' : ''}`}>
        <button type="button" className="top-control-button" onClick={() => moveTo(other)}>
          <svg viewBox="0 0 24 24" aria-hidden="true">{SIDE_ICON[other]}</svg>
          Move {other}
        </button>
        <button type="button" className="top-control-button" onClick={() => setWatchOpen(true)}>
          Open game
        </button>
        <button type="button" className="top-control-button" onClick={fullWindow}>
          Full window
        </button>
      </div>
      {watchOpen
        ? createPortal(<WatchSetup side={current} onMove={moveTo} onClose={() => setWatchOpen(false)} />, document.body)
        : null}
    </>
  );
}

/** The full page while the side window runs LagKing. */
export function SideHandoff({ win, onBack }: { win: Window; onBack: () => void }) {
  useEffect(() => {
    const self = window as Window & { [HANDOFF_FLAG]?: boolean };
    self[HANDOFF_FLAG] = true;
    const timer = window.setInterval(() => {
      if (win.closed) onBack();
    }, 700);
    return () => {
      window.clearInterval(timer);
      delete self[HANDOFF_FLAG];
    };
  }, [win, onBack]);
  return (
    <main className="side-handoff">
      <h1>LagKing is in the side window</h1>
      <p>Open your game from LagKing's Open game button, or put it in the rest of the screen yourself. {arrangeHelp(arrangePlatform(navigator.userAgent))}</p>
      <div className="side-handoff-actions">
        <button type="button" className="top-control-button" onClick={() => win.focus()}>Show the side window</button>
        <button type="button" className="top-control-button" onClick={() => { win.close(); onBack(); }}>Bring it back here</button>
      </div>
    </main>
  );
}
